import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import StarIcon from "@mui/icons-material/Star";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { LojaDataGrid, LojaPageContainer } from "../common";
import {
  getAllAvaliacoes,
  getAllAvaliacoesPedidos,
  updateStatusAvaliacaoPedido,
  updateStatusAvaliacaoProduto,
} from "services/lojaAvaliacoes";
import type {
  IAvaliacaoPedido,
  IAvaliacaoProduto,
  StatusAvaliacao,
} from "interfaces/loja";
import { useMaterialUIController, setMessage } from "context";
import AvaliacaoDetailDialog from "./components/AvaliacaoDetailDialog";
import {
  AVALIACOES_ACCENT,
  avaliacoesTabsSx,
  avaliacoesToolbarSx,
  emptyStateSx,
  fieldSx,
  gridSx,
  saveButtonSx,
  statusToggleSx,
} from "./avaliacoes.theme";

const STATUS_OPTIONS: { value: StatusAvaliacao | ""; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendentes" },
  { value: "PUBLICADA", label: "Publicadas" },
  { value: "REJEITADA", label: "Rejeitadas" },
];

function formatDateTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatPedidoNumero(row: IAvaliacaoPedido): string {
  const p = row.pedido;
  if (p?.createAt != null && p.numeroSeq != null) {
    const y = new Date(p.createAt).getFullYear();
    return `#${y}-${String(p.numeroSeq).padStart(6, "0")}`;
  }
  return row.pedidoId.length > 10 ? `${row.pedidoId.slice(0, 10)}…` : row.pedidoId;
}

function statusBadgeSx(status: StatusAvaliacao | undefined) {
  const s = status ?? "PENDENTE";
  if (s === "PUBLICADA") return { bg: "success.light", fg: "success.dark" as const };
  if (s === "PENDENTE") return { bg: "warning.light", fg: "warning.dark" as const };
  return { bg: "error.light", fg: "error.dark" as const };
}

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? AVALIACOES_ACCENT : "text.primary"}
        sx={{ fontSize: { xs: "1.25rem", sm: "2rem" } }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function LojaAvaliacoes() {
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const [qProd, setQProd] = useState("");
  const [statusProd, setStatusProd] = useState<StatusAvaliacao | "">("");
  const [rowsProd, setRowsProd] = useState<IAvaliacaoProduto[]>([]);
  const [metaProd, setMetaProd] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
  });
  const [loadingProd, setLoadingProd] = useState(true);
  const [pendProd, setPendProd] = useState(0);
  const [reloadProd, setReloadProd] = useState(0);

  const [qPed, setQPed] = useState("");
  const [statusPed, setStatusPed] = useState<StatusAvaliacao | "">("");
  const [rowsPed, setRowsPed] = useState<IAvaliacaoPedido[]>([]);
  const [metaPed, setMetaPed] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 1,
  });
  const [loadingPed, setLoadingPed] = useState(true);
  const [pendPed, setPendPed] = useState(0);
  const [reloadPed, setReloadPed] = useState(0);

  const [savingId, setSavingId] = useState<string | null>(null);
  const [modalProd, setModalProd] = useState<IAvaliacaoProduto | null>(null);
  const [modalPed, setModalPed] = useState<IAvaliacaoPedido | null>(null);

  useEffect(() => {
    setModalProd((prev) => {
      if (!prev) return null;
      return rowsProd.find((r) => r.id === prev.id) ?? prev;
    });
  }, [rowsProd]);

  useEffect(() => {
    setModalPed((prev) => {
      if (!prev) return null;
      return rowsPed.find((r) => r.id === prev.id) ?? prev;
    });
  }, [rowsPed]);

  const refreshPendentes = useCallback(async () => {
    try {
      const [p, pe] = await Promise.all([
        getAllAvaliacoes({ page: 1, pageSize: 1, status: "PENDENTE" }),
        getAllAvaliacoesPedidos({ page: 1, pageSize: 1, status: "PENDENTE" }),
      ]);
      if (p.status === 200) setPendProd(p.data.meta?.total ?? 0);
      if (pe.status === 200) setPendPed(pe.data.meta?.total ?? 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void refreshPendentes();
  }, [refreshPendentes]);

  useEffect(() => {
    let cancelled = false;
    setLoadingProd(true);
    void (async () => {
      try {
        const res = await getAllAvaliacoes({
          page: metaProd.page,
          pageSize: metaProd.pageSize,
          status: statusProd || undefined,
          q: qProd.trim() || undefined,
        });
        if (cancelled) return;
        if (res.status === 200) {
          setRowsProd(res.data.data ?? []);
          if (res.data.meta) setMetaProd(res.data.meta);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setMessage(dispatch, {
            open: true,
            severity: "error",
            text: e instanceof Error ? e.message : "Falha ao carregar avaliações de produtos.",
          });
        }
      } finally {
        if (!cancelled) setLoadingProd(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, metaProd.page, metaProd.pageSize, qProd, statusProd, reloadProd]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPed(true);
    void (async () => {
      try {
        const res = await getAllAvaliacoesPedidos({
          page: metaPed.page,
          pageSize: metaPed.pageSize,
          status: statusPed || undefined,
          q: qPed.trim() || undefined,
        });
        if (cancelled) return;
        if (res.status === 200) {
          setRowsPed(res.data.data ?? []);
          if (res.data.meta) setMetaPed(res.data.meta);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setMessage(dispatch, {
            open: true,
            severity: "error",
            text: e instanceof Error ? e.message : "Falha ao carregar avaliações de pedidos.",
          });
        }
      } finally {
        if (!cancelled) setLoadingPed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, metaPed.page, metaPed.pageSize, qPed, statusPed, reloadPed]);

  const onPaginationProd = (model: GridPaginationModel) => {
    setMetaProd((m) => ({
      ...m,
      page: model.page + 1,
      pageSize: model.pageSize,
    }));
  };

  const onPaginationPed = (model: GridPaginationModel) => {
    setMetaPed((m) => ({
      ...m,
      page: model.page + 1,
      pageSize: model.pageSize,
    }));
  };

  const aplicarFiltrosProd = () => {
    setMetaProd((m) => ({ ...m, page: 1 }));
    setReloadProd((n) => n + 1);
  };

  const aplicarFiltrosPed = () => {
    setMetaPed((m) => ({ ...m, page: 1 }));
    setReloadPed((n) => n + 1);
  };

  const mudarStatusProduto = useCallback(
    async (id: string, status: StatusAvaliacao, label: string) => {
      setSavingId(id);
      try {
        await updateStatusAvaliacaoProduto(id, status);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: `Avaliação ${label}.`,
        });
        setReloadProd((n) => n + 1);
        await refreshPendentes();
      } catch (e: unknown) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: e instanceof Error ? e.message : "Falha ao atualizar status.",
        });
      } finally {
        setSavingId(null);
      }
    },
    [dispatch, refreshPendentes],
  );

  const mudarStatusPedido = useCallback(
    async (id: string, status: StatusAvaliacao, label: string) => {
      setSavingId(id);
      try {
        await updateStatusAvaliacaoPedido(id, status);
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: `Avaliação ${label}.`,
        });
        setReloadPed((n) => n + 1);
        await refreshPendentes();
      } catch (e: unknown) {
        setMessage(dispatch, {
          open: true,
          severity: "error",
          text: e instanceof Error ? e.message : "Falha ao atualizar status.",
        });
      } finally {
        setSavingId(null);
      }
    },
    [dispatch, refreshPendentes],
  );

  const colsProd = useMemo(
    (): GridColDef<IAvaliacaoProduto>[] => [
      {
        field: "produtoNome",
        headerName: "Produto",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => params.row.produto?.nome ?? params.row.produtoId,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.produto?.nome ?? params.row.produtoId}
          </Typography>
        ),
      },
      {
        field: "usuarioNome",
        headerName: "Cliente",
        width: 150,
        valueGetter: (params) => params.row.usuario?.nome ?? params.row.usuarioId,
      },
      {
        field: "titulo",
        headerName: "Título",
        width: 140,
        valueGetter: (params) => params.row.titulo || "—",
      },
      {
        field: "comentario",
        headerName: "Comentário",
        flex: 1,
        minWidth: 200,
        valueGetter: (params) => {
          const c = params.row.comentario?.trim();
          if (!c) return "—";
          return c.length > 100 ? `${c.slice(0, 100)}…` : c;
        },
      },
      {
        field: "nota",
        headerName: "Nota",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoProduto>) => (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <StarIcon
              sx={{
                fontSize: 18,
                color: Number(params.value) >= 4 ? "warning.main" : "grey.500",
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              {params.value ?? 0}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoProduto>) => {
          const st = (params.value as StatusAvaliacao) || "PENDENTE";
          const { bg, fg } = statusBadgeSx(st);
          return (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: bg,
                color: fg,
                fontWeight: 600,
              }}
            >
              {st}
            </Typography>
          );
        },
      },
      {
        field: "createAt",
        headerName: "Data",
        width: 140,
        valueGetter: (params) => formatDateTime(params.row.createAt),
      },
      {
        field: "ver",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoProduto>) => (
          <Tooltip title="Ver e moderar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setModalProd(params.row);
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" sx={{ color: AVALIACOES_ACCENT }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const colsPed = useMemo(
    (): GridColDef<IAvaliacaoPedido>[] => [
      {
        field: "pedidoId",
        headerName: "Pedido",
        width: 90,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params: GridRenderCellParams<IAvaliacaoPedido>) => {
          const id = params.row.pedidoId;
          if (!id) return "—";
          return (
            <Tooltip title={`Abrir pedido ${formatPedidoNumero(params.row)}`}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/loja/pedidos/${id}`);
                }}
              >
                <AssignmentOutlinedIcon fontSize="small" sx={{ color: AVALIACOES_ACCENT }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: "usuarioNome",
        headerName: "Cliente",
        width: 150,
        valueGetter: (params) => params.row.usuario?.nome ?? params.row.usuarioId,
      },
      {
        field: "comentario",
        headerName: "Comentário",
        flex: 1,
        minWidth: 220,
        valueGetter: (params) => {
          const c = params.row.comentario?.trim();
          if (!c) return "—";
          return c.length > 100 ? `${c.slice(0, 100)}…` : c;
        },
      },
      {
        field: "nota",
        headerName: "Nota",
        width: 80,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoPedido>) => (
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <StarIcon
              sx={{
                fontSize: 18,
                color: Number(params.value) >= 4 ? "warning.main" : "grey.500",
              }}
            />
            <Typography variant="body2" fontWeight={600}>
              {params.value ?? 0}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoPedido>) => {
          const st = (params.value as StatusAvaliacao) || "PENDENTE";
          const { bg, fg } = statusBadgeSx(st);
          return (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: bg,
                color: fg,
                fontWeight: 600,
              }}
            >
              {st}
            </Typography>
          );
        },
      },
      {
        field: "createAt",
        headerName: "Data",
        width: 140,
        valueGetter: (params) => formatDateTime(params.row.createAt),
      },
      {
        field: "ver",
        headerName: "",
        width: 56,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: GridRenderCellParams<IAvaliacaoPedido>) => (
          <Tooltip title="Ver e moderar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setModalPed(params.row);
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" sx={{ color: AVALIACOES_ACCENT }} />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate],
  );

  const statsProd = {
    total: metaProd.total,
    media:
      rowsProd.length > 0
        ? (rowsProd.reduce((acc, r) => acc + (r.nota || 0), 0) / rowsProd.length).toFixed(1)
        : "—",
    positivas: rowsProd.filter((r) => (r.nota || 0) >= 4).length,
  };

  const statsPed = {
    total: metaPed.total,
    media:
      rowsPed.length > 0
        ? (rowsPed.reduce((acc, r) => acc + (r.nota || 0), 0) / rowsPed.length).toFixed(1)
        : "—",
    positivas: rowsPed.filter((r) => (r.nota || 0) >= 4).length,
  };

  const isProdTab = tabValue === 0;

  const filtrosToolbar = (
    <MDBox sx={avaliacoesToolbarSx}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <RateReviewOutlinedIcon sx={{ color: AVALIACOES_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            {isProdTab
              ? `${metaProd.total} avaliação${metaProd.total === 1 ? "" : "ões"} de produtos`
              : `${metaPed.total} avaliação${metaPed.total === 1 ? "" : "ões"} de pedidos`}
          </MDTypography>
        </Stack>
        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Status
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={isProdTab ? statusProd : statusPed}
            onChange={(_, next: StatusAvaliacao | "" | null) => {
              if (next === null) return;
              if (isProdTab) {
                setStatusProd(next);
                setMetaProd((m) => ({ ...m, page: 1 }));
              } else {
                setStatusPed(next);
                setMetaPed((m) => ({ ...m, page: 1 }));
              }
            }}
            sx={statusToggleSx}
          >
            {STATUS_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value || "all-status"} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
          <FormControl variant="outlined" size="small" fullWidth sx={fieldSx}>
            <InputLabel shrink htmlFor="avaliacoes-search">
              Buscar
            </InputLabel>
            <OutlinedInput
              id="avaliacoes-search"
              value={isProdTab ? qProd : qPed}
              onChange={(e) => (isProdTab ? setQProd : setQPed)(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  isProdTab ? aplicarFiltrosProd() : aplicarFiltrosPed();
                }
              }}
              label="Buscar"
              notched
              placeholder={isProdTab ? "Produto ou cliente…" : "Pedido ou cliente…"}
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </FormControl>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={isProdTab ? aplicarFiltrosProd : aplicarFiltrosPed}
            sx={{ ...saveButtonSx, flexShrink: 0 }}
          >
            Aplicar filtros
          </MDButton>
        </Stack>
      </Stack>
    </MDBox>
  );

  const statsRow = (
    <Grid container spacing={2} mb={3}>
      <Grid item xs={6} sm={3}>
        <StatMini
          label="Total no filtro"
          value={isProdTab ? statsProd.total : statsPed.total}
          accent
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StatMini
          label="Pendentes"
          value={isProdTab ? pendProd : pendPed}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StatMini
          label="Média (página)"
          value={isProdTab ? statsProd.media : statsPed.media}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <StatMini
          label="Nota ≥ 4 (página)"
          value={isProdTab ? statsProd.positivas : statsPed.positivas}
        />
      </Grid>
    </Grid>
  );

  const emptyState = (
    <MDBox sx={emptyStateSx}>
      <RateReviewOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
      <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
        Nenhuma avaliação encontrada
      </MDTypography>
      <MDTypography variant="body2" color="text">
        Ajuste os filtros ou aguarde novas avaliações dos clientes.
      </MDTypography>
    </MDBox>
  );

  return (
    <LojaPageContainer
      title="Avaliações"
      subtitle="Modere avaliações de produtos e pedidos. Clique na linha ou no ícone para ler o comentário completo."
    >
      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={avaliacoesTabsSx}
      >
        <Tab
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Inventory2OutlinedIcon fontSize="small" />
              <span>Produtos</span>
              {pendProd > 0 ? (
                <Chip size="small" label={pendProd} color="warning" sx={{ height: 20 }} />
              ) : null}
            </Stack>
          }
        />
        <Tab
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShoppingBagOutlinedIcon fontSize="small" />
              <span>Pedidos</span>
              {pendPed > 0 ? (
                <Chip size="small" label={pendPed} color="warning" sx={{ height: 20 }} />
              ) : null}
            </Stack>
          }
        />
      </Tabs>

      {filtrosToolbar}
      {statsRow}

      {isProdTab ? (
        !loadingProd && rowsProd.length === 0 ? (
          emptyState
        ) : (
          <LojaDataGrid
            rows={rowsProd}
            getRowId={(r) => r.id}
            columns={colsProd}
            loading={loadingProd}
            paginationMode="server"
            rowCount={metaProd.total}
            pageSizeOptions={[10, 20, 25, 50, 100]}
            paginationModel={{
              page: metaProd.page - 1,
              pageSize: metaProd.pageSize,
            }}
            onPaginationModelChange={onPaginationProd}
            onRowClick={(p) => setModalProd(p.row)}
            sx={gridSx}
          />
        )
      ) : !loadingPed && rowsPed.length === 0 ? (
        emptyState
      ) : (
        <LojaDataGrid
          rows={rowsPed}
          getRowId={(r) => r.id}
          columns={colsPed}
          loading={loadingPed}
          paginationMode="server"
          rowCount={metaPed.total}
          pageSizeOptions={[10, 20, 25, 50, 100]}
          paginationModel={{
            page: metaPed.page - 1,
            pageSize: metaPed.pageSize,
          }}
          onPaginationModelChange={onPaginationPed}
          onRowClick={(p) => setModalPed(p.row)}
          sx={gridSx}
        />
      )}

      {modalProd ? (
        <AvaliacaoDetailDialog
          open
          tipo="produto"
          data={modalProd}
          savingId={savingId}
          onClose={() => setModalProd(null)}
          onStatusChange={mudarStatusProduto}
        />
      ) : null}

      {modalPed ? (
        <AvaliacaoDetailDialog
          open
          tipo="pedido"
          data={modalPed}
          pedidoLabel={formatPedidoNumero(modalPed)}
          savingId={savingId}
          onClose={() => setModalPed(null)}
          onStatusChange={mudarStatusPedido}
        />
      ) : null}
    </LojaPageContainer>
  );
}
